import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Configs from '@/Configs';
import { default as Footer } from '@/common/Footer';
import * as apiCalls from '@/common/apiCalls';
import { useAuth } from '@/common/appContext';
import { useData } from '@/common/dataContext';
import Toaster from '@/common/support/toaster';
import { hexToRGBA } from '@/common/utils/colorUtils.js';
import { UnifiedIcon } from '@/components/UnifiedIcon';
import { KincodeButton } from '@/components/button/KincodeButton';
import { Pill } from '@/designSystem/atoms/Pill';
import SkeletonButton from '@/designSystem/atoms/SkeletonButton';
import SkeletonProfilePicture from '@/designSystem/atoms/SkeletonProfilePicture';
import SkeletonProgressBar from '@/designSystem/atoms/SkeletonProgressBar';
import SkeletonText from '@/designSystem/atoms/SkeletonText';
import SkeletonTitle from '@/designSystem/atoms/SkeletonTitle';
import { KebabMenu } from '@/designSystem/molecules/KebabMenu';
import ConfettiAnimation from '@/widgets/confetti/ConfettiAnimation';
import { getCompanyBrandFromPreferences } from '@/utils/companyBrand';
import {
  authenticatedGetPulseSurvey,
  authenticatedPostPulseSurveyAnswer,
  authenticatedSurveyErrorI18nKey,
} from '@/views/survey/core/apiCalls';
import { buildStandardPulseScaleOptions } from '@/views/pulse/core/buildStandardPulseScaleOptions';
import {
  SURVEY_DRAFT_STORAGE_VERSION,
  SURVEY_STEPPER_AUTO_ADVANCE_MS,
  SURVEY_VIEW_MODE_LIST,
  SURVEY_VIEW_MODE_STEPPER,
  buildSurveyAnswerPayload,
  buildSurveyDraftStorageKey,
  buildSurveyQuestionTaxonomyPills,
  clearSurveyDraft,
  formatDateLabel,
  formatSurveyAnswerDisplayLabel,
  getLocalizedPulseQuestionText,
  getLocalizedPulseScaleResponseLabels,
  getQuestionKey,
  getSurveyRnaLabel,
  hasSurveyAnswerValue,
  isQuestionAnswered,
  isSafeRemoteImageUrl,
  linkifySurveyDescriptionPlainText,
  normalizeDraftAnswers,
  readSurveyDraft,
  readSurveyViewMode,
  saveSurveyDraft,
  saveSurveyViewMode,
  shouldAutoAdvanceSurveyStepper,
} from '@/views/survey/pulseSurveyHelpers';
import { useToastState } from '@/common/uiShellContext';

const surveyPageClassName = 'flex h-dvh w-full flex-col overflow-y-auto text-left';

const surveyQuestionCardClassName =
  'rounded-kc border border-border-default bg-white shadow-kcLightWidgets dark:border-border-default-dark dark:bg-slate-800 dark:shadow-kcDarkWidgets';

const surveyMutedTextClassName = 'text-kc-gray-dark dark:text-kc-gray-mid';

/** Secondary copy on the branded page gradient — needs stronger contrast than card muted text. */
const surveyHeroMutedTextClassName = 'text-slate-700 dark:text-slate-300';

const surveyLegalFooterClassName =
  'mt-auto w-full shrink-0 px-4 [&_.footer]:mt-0 [&_.footer]:pt-0 [&_.footer>div]:gap-2 [&_.footer>div]:py-3';

const skeletonMutedClassName = 'dark:bg-slate-600';

/**
 * Layout-matching skeleton for the initial pulse survey fetch.
 * @param {{ pageBackgroundStyle: React.CSSProperties }} props
 */
function PulseSurveyLoadingSkeleton({ pageBackgroundStyle }) {
  return (
    <div
      aria-busy="true"
      className={surveyPageClassName}
      data-testid="survey-pulse-survey-skeleton"
      style={pageBackgroundStyle}
    >
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-5 px-4 py-6 lg:px-6 lg:py-8">
        <section className="flex shrink-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <SkeletonProfilePicture className={skeletonMutedClassName} size="lg" />
            <div className="min-w-0 space-y-2 text-left">
              <SkeletonTitle className={skeletonMutedClassName} size="h4" width="w-56" />
              <SkeletonText className={skeletonMutedClassName} size="sm" width="w-72 max-w-full" />
              <SkeletonText className={skeletonMutedClassName} size="xs" width="w-40" />
            </div>
          </div>
          <SkeletonText className={skeletonMutedClassName} size="sm" width="w-48" />
        </section>

        <div className={`overflow-hidden ${surveyQuestionCardClassName}`}>
          <div className="shrink-0 space-y-3 border-b border-border-default px-5 py-4 dark:border-border-default-dark lg:px-6">
            <div className="flex items-center justify-between gap-3">
              <SkeletonText className={skeletonMutedClassName} size="xs" width="w-16" />
              <SkeletonText className={skeletonMutedClassName} size="xs" width="w-28" />
            </div>
            <SkeletonProgressBar className={skeletonMutedClassName} height="thin" width="w-full" />
          </div>

          <div className="space-y-5 px-5 py-6 lg:px-6 lg:py-7">
            <SkeletonTitle className={skeletonMutedClassName} size="h4" width="w-full max-w-md" />
            <div className="flex flex-col gap-3">
              <SkeletonButton className={skeletonMutedClassName} pill size="lg" width="w-full" />
              <SkeletonButton className={skeletonMutedClassName} pill size="lg" width="w-full" />
              <SkeletonButton className={skeletonMutedClassName} pill size="lg" width="w-full" />
              <SkeletonButton className={skeletonMutedClassName} pill size="lg" width="w-3/4" />
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-4 border-t border-border-default px-5 py-4 dark:border-border-default-dark sm:flex-row sm:items-center sm:justify-between lg:px-6">
            <SkeletonText className={skeletonMutedClassName} size="sm" width="w-48" />
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <SkeletonButton className={skeletonMutedClassName} size="base" width="w-20" />
              <SkeletonButton className={skeletonMutedClassName} pill size="lg" width="w-28" />
            </div>
          </div>
        </div>
      </div>

      <div className={surveyLegalFooterClassName}>
        <Footer color="bg-transparent" />
      </div>
    </div>
  );
}

export function PulseSurvey({ mode = 'invite', routeCampaignID = null } = {}) {
  const { t, i18n } = useTranslation();
  const { setToasterMessage } = useData();
  const { toasterMessage } = useToastState();
  const {
    companyID: authCompanyID,
    employeeID: authEmployeeID,
    token: authToken,
    correlationId,
  } = useAuth();

  const isAuthenticatedMode = mode === 'authenticated';

  const signedSearch = window.location.search;
  const params = new URLSearchParams(signedSearch);
  const campaignID = isAuthenticatedMode ? routeCampaignID : params.get('campaignID');
  const companyID = isAuthenticatedMode ? authCompanyID : params.get('companyID');
  const employeeID = isAuthenticatedMode ? authEmployeeID : params.get('employeeID');
  const questionDate = isAuthenticatedMode ? null : params.get('questionDate');
  const token = isAuthenticatedMode ? authToken : params.get('token');

  const [campaign, setCampaign] = useState(null);
  const [hasRequestError, setHasRequestError] = useState(false);
  const [requestErrorMessage, setRequestErrorMessage] = useState(/** @type {string | null} */ (null));
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [answersByQuestionId, setAnswersByQuestionId] = useState({});
  const [hasHydratedDraft, setHasHydratedDraft] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [wasAlreadyCompleted, setWasAlreadyCompleted] = useState(false);
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const [confettiLevel, setConfettiLevel] = useState('small');
  const [viewMode, setViewMode] = useState(readSurveyViewMode);
  /** Non-null while a stepper auto-advance is pending (value is opaque nonce). */
  const [stepperAutoAdvanceNonce, setStepperAutoAdvanceNonce] = useState(
    /** @type {number | null} */ (null)
  );

  const isListView = viewMode === SURVEY_VIEW_MODE_LIST;
  const questions = useMemo(() => campaign?.questions ?? [], [campaign]);
  const reviewStepIndex = useMemo(() => questions.length, [questions.length]);
  const isReviewStep = !isListView && questions.length > 0 && currentStep === reviewStepIndex;
  const currentQuestionEntry =
    !isReviewStep && !isListView ? (questions[currentStep] ?? null) : null;
  const currentQuestionKey = currentQuestionEntry
    ? getQuestionKey(currentQuestionEntry, currentStep)
    : null;

  const companyPreferences = useMemo(() => {
    const raw = campaign?.companyPreferences;
    if (Array.isArray(raw)) {
      return raw[0] ?? null;
    }
    return raw ?? null;
  }, [campaign?.companyPreferences]);

  const { colorHex, buttonTheme } = useMemo(
    () => getCompanyBrandFromPreferences(companyPreferences),
    [companyPreferences]
  );

  const pageBackgroundStyle = useMemo(
    () => ({
      backgroundImage: `linear-gradient(to bottom, ${hexToRGBA(colorHex, 0.42)}, ${hexToRGBA(colorHex, 0.16)})`,
    }),
    [colorHex]
  );

  const companyLogoUrl = useMemo(() => {
    const rawLogoUrl = companyPreferences?.personalization?.logoLarge;
    return isSafeRemoteImageUrl(rawLogoUrl) ? rawLogoUrl : Configs.companyLogoLarge;
  }, [companyPreferences?.personalization?.logoLarge]);

  const draftStorageKey = useMemo(() => {
    if (!campaignID || !companyID || !employeeID) {
      return null;
    }

    return buildSurveyDraftStorageKey(campaignID, companyID, employeeID);
  }, [campaignID, companyID, employeeID]);

  // Progress counts concrete answers only — blank optional opens must not inflate "X of Y answered".
  const answeredCount = useMemo(
    () =>
      questions.filter((questionEntry, index) =>
        hasSurveyAnswerValue(
          questionEntry,
          answersByQuestionId[getQuestionKey(questionEntry, index)]
        )
      ).length,
    [answersByQuestionId, questions]
  );

  const readyToSubmitCount = useMemo(
    () =>
      questions.filter((questionEntry, index) =>
        isQuestionAnswered(questionEntry, answersByQuestionId[getQuestionKey(questionEntry, index)])
      ).length,
    [answersByQuestionId, questions]
  );

  const pendingCount = Math.max(questions.length - answeredCount, 0);
  const progressPercentage =
    questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;
  const canSubmitSurvey = readyToSubmitCount >= questions.length;

  const currentQuestionAnswered =
    currentQuestionEntry != null &&
    isQuestionAnswered(currentQuestionEntry, answersByQuestionId[currentQuestionKey]);

  const closeToast = useCallback(() => {
    setToasterMessage(null);
  }, [setToasterMessage]);

  const triggerConfetti = useCallback((level) => {
    setConfettiLevel(level);
    setConfettiTrigger((previous) => previous + 1);
  }, []);

  const handleViewModeChange = useCallback(
    (nextViewMode) => {
      if (nextViewMode !== SURVEY_VIEW_MODE_LIST && nextViewMode !== SURVEY_VIEW_MODE_STEPPER) {
        return;
      }

      setStepperAutoAdvanceNonce(null);

      setViewMode(nextViewMode);
      saveSurveyViewMode(nextViewMode);

      if (nextViewMode === SURVEY_VIEW_MODE_STEPPER) {
        setCurrentStep((previous) => Math.min(previous, reviewStepIndex));
      }
    },
    [reviewStepIndex]
  );

  const viewModeMenuItems = useMemo(
    () => [
      {
        id: 'view-mode-section',
        type: 'section',
        label: t('pulse.survey.view.ariaLabel'),
        items: [
          {
            id: SURVEY_VIEW_MODE_STEPPER,
            label: t('pulse.survey.view.stepper'),
            icon: 'ListNumbers',
            iconLibrary: 'phosphor',
            onClick: () => handleViewModeChange(SURVEY_VIEW_MODE_STEPPER),
            ariaLabel: t('pulse.survey.view.stepper'),
            data: { isActive: viewMode === SURVEY_VIEW_MODE_STEPPER },
            keepMenuOpen: true,
          },
          {
            id: SURVEY_VIEW_MODE_LIST,
            label: t('pulse.survey.view.list'),
            icon: 'ListBullets',
            iconLibrary: 'phosphor',
            onClick: () => handleViewModeChange(SURVEY_VIEW_MODE_LIST),
            ariaLabel: t('pulse.survey.view.list'),
            data: { isActive: viewMode === SURVEY_VIEW_MODE_LIST },
            keepMenuOpen: true,
          },
        ],
      },
    ],
    [handleViewModeChange, t, viewMode]
  );

  const requestSurvey = useCallback(async () => {
    setIsLoading(true);
    setHasRequestError(false);
    setRequestErrorMessage(null);

    try {
      const response = isAuthenticatedMode
        ? await authenticatedGetPulseSurvey(
            companyID,
            employeeID,
            token,
            campaignID,
            correlationId
          )
        : await apiCalls.unauthenticatedGetPulseSurvey(
            companyID,
            employeeID,
            token,
            campaignID,
            questionDate,
            null,
            signedSearch
          );

      if (Configs.devEnvironment) {
        console.log('[pulseSurvey.jsx][requestSurvey()] Response:', response);
      }

      const authErrorStatus =
        isAuthenticatedMode &&
        response &&
        typeof response === 'object' &&
        !Array.isArray(response) &&
        typeof response.status === 'number'
          ? response.status
          : null;
      const responseStatus = authErrorStatus ?? (typeof response === 'number' ? response : null);

      if (
        responseStatus === 500 ||
        responseStatus === 404 ||
        responseStatus === 403 ||
        responseStatus === 401 ||
        !Array.isArray(response) ||
        !response[0]
      ) {
        const errorMessage = isAuthenticatedMode
          ? t(
              responseStatus === 401
                ? 'pulse.survey.link.invalid'
                : authenticatedSurveyErrorI18nKey(
                    authErrorStatus != null ? response.reason : null
                  )
            )
          : responseStatus === 403 || responseStatus === 401
            ? t('pulse.survey.link.invalid')
            : t('pulse.survey.error');
        setHasRequestError(true);
        setRequestErrorMessage(errorMessage);
        setToasterMessage({
          message: errorMessage,
          type: Configs.errorToaster,
        });
        return;
      }

      const nextCampaign = response[0];
      setCampaign(nextCampaign);

      if (nextCampaign?.alreadyCompleted) {
        clearSurveyDraft(draftStorageKey);
        setWasAlreadyCompleted(true);
        setIsSubmitted(true);
        setHasHydratedDraft(true);
        setToasterMessage({
          message: t('pulse.survey.answered'),
          type: Configs.infoToaster,
        });
      }
    } catch (error) {
      console.error('[pulseSurvey.jsx][requestSurvey()] Error fetching survey:', error);
      setHasRequestError(true);
      setRequestErrorMessage(t('error'));
      setToasterMessage({ message: t('error'), type: Configs.errorToaster });
    } finally {
      setIsLoading(false);
    }
  }, [
    campaignID,
    companyID,
    correlationId,
    draftStorageKey,
    employeeID,
    isAuthenticatedMode,
    questionDate,
    setToasterMessage,
    signedSearch,
    t,
    token,
  ]);

  useEffect(() => {
    if (!campaignID || !companyID || !employeeID || campaign || hasRequestError) {
      return;
    }
    if (isAuthenticatedMode && !token) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- bootstrap survey fetch once params settle
    requestSurvey();
  }, [
    campaign,
    campaignID,
    companyID,
    employeeID,
    hasRequestError,
    isAuthenticatedMode,
    requestSurvey,
    token,
  ]);

  useEffect(() => {
    if (!campaign || hasHydratedDraft || campaign?.alreadyCompleted) {
      return;
    }

    const draft = readSurveyDraft(draftStorageKey);
    const normalizedAnswers = normalizeDraftAnswers(draft?.answersByQuestionId, questions);
    const nextStep =
      typeof draft?.currentStep === 'number'
        ? Math.min(Math.max(draft.currentStep, 0), reviewStepIndex)
        : 0;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate answers/step from localStorage once campaign loads
    setAnswersByQuestionId(normalizedAnswers);
    setCurrentStep(nextStep);
    setHasHydratedDraft(true);
  }, [campaign, draftStorageKey, hasHydratedDraft, questions, reviewStepIndex]);

  useEffect(() => {
    if (!campaign || !hasHydratedDraft || !draftStorageKey || isSubmitted) {
      return;
    }

    saveSurveyDraft(draftStorageKey, {
      version: SURVEY_DRAFT_STORAGE_VERSION,
      campaignID,
      companyID,
      employeeID,
      currentStep,
      answersByQuestionId,
      updatedAt: new Date().toISOString(),
    });
  }, [
    answersByQuestionId,
    campaign,
    campaignID,
    companyID,
    currentStep,
    draftStorageKey,
    employeeID,
    hasHydratedDraft,
    isSubmitted,
  ]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep, isSubmitted]);

  useEffect(() => {
    if (stepperAutoAdvanceNonce == null || isListView || isSubmitted || wasAlreadyCompleted) {
      return;
    }

    const timerId = setTimeout(() => {
      setStepperAutoAdvanceNonce(null);
      setCurrentStep((previous) => Math.min(previous + 1, reviewStepIndex));
    }, SURVEY_STEPPER_AUTO_ADVANCE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [
    isListView,
    isSubmitted,
    reviewStepIndex,
    stepperAutoAdvanceNonce,
    wasAlreadyCompleted,
  ]);

  const scheduleStepperAutoAdvance = useCallback(
    (questionEntry, questionIndex, nextAnswerValue) => {
      if (isListView || isSubmitted || wasAlreadyCompleted) {
        return;
      }
      if (questionIndex !== currentStep) {
        return;
      }
      if (!shouldAutoAdvanceSurveyStepper(questionEntry)) {
        return;
      }
      if (!isQuestionAnswered(questionEntry, nextAnswerValue)) {
        return;
      }

      setStepperAutoAdvanceNonce(Date.now());
    },
    [currentStep, isListView, isSubmitted, wasAlreadyCompleted]
  );

  const updateSingleAnswer = useCallback(
    (questionKey, nextValue, questionEntry = null, questionIndex = null) => {
      setAnswersByQuestionId((previous) => ({
        ...previous,
        [questionKey]: nextValue,
      }));

      if (questionEntry != null && questionIndex != null) {
        scheduleStepperAutoAdvance(questionEntry, questionIndex, nextValue);
      }
    },
    [scheduleStepperAutoAdvance]
  );

  const handleMultiselectChange = useCallback(
    (questionKey, option, maxOptions, questionEntry = null, questionIndex = null) => {
      const currentSelections = Array.isArray(answersByQuestionId[questionKey])
        ? answersByQuestionId[questionKey]
        : [];

      let nextSelections;
      if (currentSelections.includes(option)) {
        nextSelections = currentSelections.filter((item) => item !== option);
      } else if (!maxOptions || currentSelections.length < maxOptions) {
        nextSelections = [...currentSelections, option];
      } else {
        setToasterMessage({
          message: t('pulse.survey.max.options', { max: maxOptions }),
          type: Configs.warningToaster,
        });
        return;
      }

      setAnswersByQuestionId((previous) => ({
        ...previous,
        [questionKey]: nextSelections,
      }));

      if (questionEntry != null && questionIndex != null) {
        scheduleStepperAutoAdvance(questionEntry, questionIndex, nextSelections);
      }
    },
    [answersByQuestionId, scheduleStepperAutoAdvance, setToasterMessage, t]
  );

  const handleNext = useCallback(() => {
    if (!currentQuestionEntry || !currentQuestionKey) {
      return;
    }

    if (!isQuestionAnswered(currentQuestionEntry, answersByQuestionId[currentQuestionKey])) {
      setToasterMessage({
        message: t('pulse.survey.missing.answers'),
        type: Configs.warningToaster,
      });
      return;
    }

    setStepperAutoAdvanceNonce(null);

    setCurrentStep(Math.min(currentStep + 1, reviewStepIndex));
  }, [
    answersByQuestionId,
    currentQuestionEntry,
    currentQuestionKey,
    currentStep,
    reviewStepIndex,
    setToasterMessage,
    t,
  ]);

  const handlePrevious = useCallback(() => {
    setStepperAutoAdvanceNonce(null);
    setCurrentStep((previous) => Math.max(previous - 1, 0));
  }, []);

  const showAlreadyCompletedState = useCallback(() => {
    clearSurveyDraft(draftStorageKey);
    setWasAlreadyCompleted(true);
    setIsSubmitted(true);
    setHasHydratedDraft(true);
  }, [draftStorageKey]);

  const handleSubmitSurvey = useCallback(async () => {
    if (wasAlreadyCompleted || campaign?.alreadyCompleted) {
      showAlreadyCompletedState();
      return;
    }

    const hasMissingAnswers = questions.some((questionEntry, index) => {
      const questionKey = getQuestionKey(questionEntry, index);
      return !isQuestionAnswered(questionEntry, answersByQuestionId[questionKey]);
    });

    if (hasMissingAnswers) {
      setToasterMessage({
        message: t('pulse.survey.missing.answers'),
        type: Configs.warningToaster,
      });
      return;
    }

    setIsLoading(true);
    setStepperAutoAdvanceNonce(null);

    try {
      const surveyAnswer = buildSurveyAnswerPayload(
        campaign,
        employeeID,
        questions,
        answersByQuestionId
      );
      const response = isAuthenticatedMode
        ? await authenticatedPostPulseSurveyAnswer(
            companyID,
            employeeID,
            token,
            campaignID,
            { answers: surveyAnswer },
            correlationId
          )
        : await apiCalls.unauthenticatedPostPulseSurveyAnswer(
            companyID,
            employeeID,
            token,
            campaignID,
            questionDate,
            { answers: surveyAnswer },
            null,
            signedSearch
          );

      if (Configs.devEnvironment) {
        console.log('[pulseSurvey.jsx][handleSubmitSurvey()] Response:', response);
      }

      const authErrorStatus =
        isAuthenticatedMode &&
        response &&
        typeof response === 'object' &&
        typeof response.status === 'number'
          ? response.status
          : null;
      const authErrorReason =
        isAuthenticatedMode &&
        response &&
        typeof response === 'object' &&
        typeof response.reason === 'string'
          ? response.reason
          : null;
      const responseStatus = authErrorStatus ?? (typeof response === 'number' ? response : null);

      if (responseStatus === 409 || authErrorReason === 'already_completed') {
        showAlreadyCompletedState();
        setToasterMessage({
          message: t('pulse.survey.answered'),
          type: Configs.infoToaster,
        });
        return;
      }

      if (
        responseStatus === 500 ||
        responseStatus === 404 ||
        responseStatus === 403 ||
        responseStatus === 401
      ) {
        setToasterMessage({
          message: isAuthenticatedMode
            ? t(
                responseStatus === 401
                  ? 'pulse.survey.link.invalid'
                  : authenticatedSurveyErrorI18nKey(authErrorReason)
              )
            : responseStatus === 403 || responseStatus === 401
              ? t('pulse.survey.link.invalid')
              : t('pulse.survey.error'),
          type: Configs.errorToaster,
        });
        return;
      }

      if (responseStatus === 422) {
        setToasterMessage({
          message: t('error.data-validation-error'),
          type: Configs.errorToaster,
        });
        return;
      }

      clearSurveyDraft(draftStorageKey);
      setIsSubmitted(true);
      setToasterMessage({ message: t('success'), type: Configs.successToaster });
      triggerConfetti('large');
    } catch (error) {
      console.error('[pulseSurvey.jsx][handleSubmitSurvey()] Error submitting survey:', error);
      setToasterMessage({ message: t('error'), type: Configs.errorToaster });
    } finally {
      setIsLoading(false);
    }
  }, [
    answersByQuestionId,
    campaign,
    campaignID,
    companyID,
    correlationId,
    draftStorageKey,
    employeeID,
    isAuthenticatedMode,
    questionDate,
    questions,
    setToasterMessage,
    showAlreadyCompletedState,
    signedSearch,
    t,
    token,
    triggerConfetti,
    wasAlreadyCompleted,
  ]);

  const renderQuestionInput = useCallback(
    (questionEntry, questionIndex) => {
      if (!questionEntry) {
        return null;
      }

      const questionKey = getQuestionKey(questionEntry, questionIndex);
      const question = questionEntry.question;
      const currentValue = answersByQuestionId[questionKey];
      const questionAnswered = isQuestionAnswered(questionEntry, currentValue);
      const sharedOptionClassName =
        'w-full !h-auto min-h-11 !justify-start rounded-kcInner border px-3 py-2.5 text-left transition dark:border-border-default-dark [&_span]:!w-full [&_span]:!justify-start [&_span]:!whitespace-normal';

      const renderAnswerChoice = (key, label, selected, onSelect, options = {}) => {
        const isRna = Boolean(options.isRna);

        return (
          <KincodeButton
            key={key}
            aria-pressed={selected}
            className={`${sharedOptionClassName} ${
              !selected
                ? isRna
                  ? '!border-slate-300 dark:!border-slate-600 border-dashed'
                  : '!border-slate-300 dark:!border-slate-600'
                : ''
            }`.trim()}
            colorHex={colorHex}
            data-testid={
              isRna ? 'survey-pulse-survey-answer-rna' : `survey-pulse-survey-answer-${key}`
            }
            theme={buttonTheme}
            variant={selected ? 'primary' : 'secondary'}
            onClick={onSelect}
          >
            <div className="flex w-full items-start justify-start gap-3 text-left whitespace-normal">
              <div
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                  selected
                    ? 'border-current bg-white/35 text-current'
                    : 'border-slate-300 text-transparent dark:border-slate-600'
                }`}
              >
                <UnifiedIcon className="h-3.5 w-3.5" name="Check" />
              </div>
              <span className="type-body flex-1 text-left whitespace-normal">{label}</span>
            </div>
          </KincodeButton>
        );
      };

      if (question?.type === 'open') {
        return (
          <textarea
            className="type-body min-h-[8rem] w-full rounded-kcInner border border-border-default bg-white px-4 py-3 text-left text-kc-black shadow-sm outline-none transition placeholder:text-kc-gray-mid focus:ring-4 dark:border-border-default-dark dark:bg-slate-900 dark:text-kc-gray-light"
            id={questionKey}
            name={questionKey}
            placeholder={t('pulse.survey.open.placeholder')}
            style={{
              borderColor: questionAnswered ? colorHex : undefined,
              boxShadow: questionAnswered ? `0 0 0 4px ${hexToRGBA(colorHex, 0.12)}` : undefined,
              accentColor: colorHex,
            }}
            value={typeof currentValue === 'string' ? currentValue : ''}
            onChange={(event) => updateSingleAnswer(questionKey, event.target.value)}
          />
        );
      }

      if (question?.type === 'multiselect' && Array.isArray(question?.options)) {
        return (
          <div className="space-y-3">
            {question.options.map((option, optionIndex) => {
              const selected = Array.isArray(currentValue) && currentValue.includes(option);
              return renderAnswerChoice(
                `${questionKey}-option-${optionIndex}`,
                option,
                selected,
                () =>
                  handleMultiselectChange(
                    questionKey,
                    option,
                    question?.maxOptions,
                    questionEntry,
                    questionIndex
                  )
              );
            })}
          </div>
        );
      }

      if (question?.type === 'eNPS') {
        const enpsOptions = Array.from({ length: 10 }, (_, index) => {
          const score = 10 - index;
          return { value: String(score), label: String(score) };
        });
        return (
          <div className="space-y-3" data-testid={`survey-enps-options-${questionKey}`}>
            {enpsOptions.map((option) => {
              const selected = currentValue === option.value;
              return renderAnswerChoice(
                `${questionKey}-${option.value}`,
                option.label,
                selected,
                () => updateSingleAnswer(questionKey, option.value, questionEntry, questionIndex)
              );
            })}
          </div>
        );
      }

      // Standard scale: answerIDs follow non-empty Resp count (k…1), not a hardcoded 5…1.
      // Labels follow the user's UI language (EN `Resp*_String` vs ES `*_ESP`), with fallback.
      const answerOptions = buildStandardPulseScaleOptions(
        getLocalizedPulseScaleResponseLabels(question, i18n.language)
      ).map((option) => ({ value: option.value, label: option.label }));

      const canShowRatherNotAnswer = !(
        question?.Rather_Not_Answer === 0 || question?.Rather_Not_Answer === null
      );

      if (canShowRatherNotAnswer) {
        answerOptions.push({
          value: 'rna',
          label: getSurveyRnaLabel(question, t('pulse.survey.rna')),
          isRna: true,
        });
      }

      return (
        <div className="space-y-3">
          {answerOptions.map((option) => {
            const selected = currentValue === option.value;
            return renderAnswerChoice(
              `${questionKey}-${option.value}`,
              option.label,
              selected,
              () => updateSingleAnswer(questionKey, option.value, questionEntry, questionIndex),
              { isRna: option.isRna === true }
            );
          })}
        </div>
      );
    },
    [
      answersByQuestionId,
      buttonTheme,
      colorHex,
      handleMultiselectChange,
      i18n.language,
      t,
      updateSingleAnswer,
    ]
  );

  /**
   * @param {{ question?: Record<string, unknown> } | null | undefined} questionEntry
   */
  const renderQuestionTaxonomyPills = useCallback((questionEntry) => {
    const pills = buildSurveyQuestionTaxonomyPills(questionEntry?.question);
    if (pills.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-2" data-testid="survey-pulse-survey-taxonomy-pills">
        {pills.map((pill) => (
          <Pill
            key={pill.id}
            data-testid={`survey-pulse-survey-taxonomy-${pill.id}`}
            size="sm"
            title={pill.id}
            tone="neutral"
          >
            {pill.label}
          </Pill>
        ))}
      </div>
    );
  }, []);

  if (!campaignID || !companyID || !employeeID) {
    return (
      <div
        className={surveyPageClassName}
        data-testid="survey-pulse-survey"
        style={pageBackgroundStyle}
      >
        <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 items-center p-4">
          <div className={`w-full p-8 ${surveyQuestionCardClassName}`}>
            <div className="flex flex-col gap-4 text-center">
              <h1 className="type-h3 text-kc-black dark:text-kc-gray-light">{t('error.404')}</h1>
              <p className={`type-body ${surveyMutedTextClassName}`}>{t('pulse.survey.error')}</p>
            </div>
          </div>
        </div>
        <div className={surveyLegalFooterClassName}>
          <Footer color="bg-transparent" />
        </div>
      </div>
    );
  }

  if (!campaign && !hasRequestError) {
    return <PulseSurveyLoadingSkeleton pageBackgroundStyle={pageBackgroundStyle} />;
  }

  if (hasRequestError || !campaign) {
    const errorBodyMessage = requestErrorMessage || t('pulse.survey.error');
    const errorTitle = isAuthenticatedMode
      ? t('pulse.survey.accessDeniedTitle')
      : t('error.404');
    return (
      <div
        className={surveyPageClassName}
        data-testid={
          isAuthenticatedMode ? 'survey-pulse-survey-authenticated' : 'survey-pulse-survey'
        }
        style={pageBackgroundStyle}
      >
        <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 items-center p-4 lg:p-10">
          <div
            className={`w-full p-8 ${surveyQuestionCardClassName}`}
            data-testid="survey-pulse-survey-error"
          >
            {toasterMessage ? (
              <Toaster
                message={toasterMessage}
                onClose={closeToast}
                timeout={Configs.toasterTimeout}
              />
            ) : null}
            <div className="flex flex-col gap-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300">
                <UnifiedIcon className="h-8 w-8" name="SmileyXEyes" />
              </div>
              <h1 className="type-h3 text-kc-black dark:text-kc-gray-light">{errorTitle}</h1>
              <p className={`type-body ${surveyMutedTextClassName}`}>{errorBodyMessage}</p>
              <div className="pt-2">
                <KincodeButton
                  colorHex={Configs.companyColor}
                  onClick={() => window.location.assign('https://platform.kincode.app')}
                >
                  {t('pulse.survey.backToPlatform')}
                </KincodeButton>
              </div>
            </div>
          </div>
        </div>
        <div className={surveyLegalFooterClassName}>
          <Footer color="bg-transparent" />
        </div>
      </div>
    );
  }

  const formattedStartDate = formatDateLabel(campaign?.startDate, i18n.language);
  const formattedEndDate = formatDateLabel(campaign?.endDate, i18n.language);

  return (
    <div
      className={surveyPageClassName}
      data-testid={
        isAuthenticatedMode ? 'survey-pulse-survey-authenticated' : 'survey-pulse-survey'
      }
      style={pageBackgroundStyle}
    >
      {confettiTrigger > 0 ? (
        <ConfettiAnimation
          key={confettiTrigger}
          level={confettiLevel}
          trigger={true}
          onComplete={() => setConfettiTrigger(0)}
        />
      ) : null}

      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-5 px-4 py-6 lg:px-6 lg:py-8">
        {toasterMessage ? (
          <Toaster message={toasterMessage} onClose={closeToast} timeout={Configs.toasterTimeout} />
        ) : null}

        <section
          className="flex shrink-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
          data-testid="survey-pulse-survey-hero"
        >
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-kcInner bg-white ring-1 ring-border-default dark:bg-slate-900 dark:ring-border-default-dark">
              <img alt="Company Logo" className="h-8 w-8 object-contain" src={companyLogoUrl} />
            </div>
            <div className="min-w-0 space-y-1 text-left">
              <h1 className="type-h4 text-pretty text-kc-black dark:text-kc-gray-light">
                {campaign?.campaignName || t('pulse.survey.title')}
              </h1>
              {campaign?.campaignName ? (
                <p className={`type-body text-pretty ${surveyHeroMutedTextClassName}`}>
                  {t('pulse.survey.title')}
                </p>
              ) : null}
              {campaign?.companyName ? (
                <p className={`type-body text-pretty ${surveyHeroMutedTextClassName}`}>
                  {campaign.companyName}
                </p>
              ) : null}
            </div>
          </div>
          <p
            className={`type-body shrink-0 text-left sm:pt-1 sm:text-right ${surveyHeroMutedTextClassName}`}
          >
            {t('pulse.survey.startDate')} {formattedStartDate}
            {' · '}
            {t('pulse.survey.endDate')} {formattedEndDate}
          </p>
        </section>

        {!isSubmitted && String(campaign?.description ?? '').trim().length > 0 ? (
          <section
            className={`px-5 py-4 lg:px-6 ${surveyQuestionCardClassName}`}
            data-testid="survey-pulse-survey-description"
          >
            <p className={`type-body whitespace-pre-wrap text-pretty ${surveyMutedTextClassName}`}>
              {linkifySurveyDescriptionPlainText(campaign.description).map((segment, index) =>
                segment.type === 'link' ? (
                  <a
                    key={`desc-link-${index}`}
                    href={segment.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 text-kc-black dark:text-kc-gray-light"
                  >
                    {segment.value}
                  </a>
                ) : (
                  <span key={`desc-text-${index}`}>{segment.value}</span>
                )
              )}
            </p>
          </section>
        ) : null}

        {isSubmitted ? (
          <section
            className={`flex flex-col items-center justify-center px-6 py-8 text-center ${surveyQuestionCardClassName}`}
            data-testid="survey-pulse-survey-success"
          >
            <div
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ backgroundColor: hexToRGBA(colorHex, 0.16) }}
            >
              <UnifiedIcon
                className="h-8 w-8"
                name={wasAlreadyCompleted ? 'CheckCircle' : 'Confetti'}
              />
            </div>
            <h2 className="type-h3 text-kc-black dark:text-kc-gray-light">
              {wasAlreadyCompleted
                ? t('pulse.survey.alreadyCompletedTitle')
                : t('pulse.survey.completeTitle')}
            </h2>
            <p className={`type-body mx-auto mt-3 max-w-2xl ${surveyMutedTextClassName}`}>
              {wasAlreadyCompleted
                ? t('pulse.survey.alreadyCompletedDescription')
                : t('pulse.survey.completeDescription')}
            </p>
            <div className="mt-6 flex justify-center">
              <KincodeButton
                colorHex={colorHex}
                theme={buttonTheme}
                onClick={() => window.location.assign('https://platform.kincode.app')}
              >
                {t('pulse.survey.backToPlatform')}
              </KincodeButton>
            </div>
          </section>
        ) : (
          <section
            className={`flex flex-col ${surveyQuestionCardClassName}`}
            data-testid="survey-pulse-survey-content"
          >
            <div className="shrink-0 border-b border-border-default px-5 py-4 dark:border-border-default-dark lg:px-6">
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className={`type-ui ${surveyMutedTextClassName}`}>
                    {isListView
                      ? t('pulse.survey.completedCount', {
                          count: answeredCount,
                          total: questions.length,
                        })
                      : isReviewStep
                        ? t('pulse.survey.reviewTitle')
                        : `${currentStep + 1}/${questions.length}`}
                  </p>
                  <div
                    className="flex flex-wrap items-center justify-end gap-3"
                    data-testid="survey-pulse-survey-view-mode"
                  >
                    <p className="type-h6 uppercase text-kc-gray-mid dark:text-kc-gray-mid">
                      {isListView
                        ? t('pulse.survey.pendingCount', { count: pendingCount })
                        : t('pulse.survey.completedCount', {
                            count: answeredCount,
                            total: questions.length,
                          })}
                    </p>
                    {!isReviewStep ? (
                      <KebabMenu
                        alignment="right"
                        ariaLabel={t('pulse.survey.view.ariaLabel')}
                        buttonSize="sm"
                        items={viewModeMenuItems}
                        menuClassName="w-56"
                        openBackgroundColor={colorHex}
                        toggleActiveColor={colorHex}
                        triggerIcon="Gear"
                        triggerIconLibrary="phosphor"
                        variant="outlined"
                      />
                    ) : null}
                  </div>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${progressPercentage}%`,
                      backgroundColor: colorHex,
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="px-5 py-6 lg:px-6 lg:py-7">
              {isListView ? (
                <div className="space-y-8 text-left">
                  {questions.map((questionEntry, index) => (
                    <div
                      key={getQuestionKey(questionEntry, index)}
                      className="space-y-4 border-b border-border-default pb-8 last:border-b-0 last:pb-0 dark:border-border-default-dark"
                    >
                      <div className="space-y-2">
                        {renderQuestionTaxonomyPills(questionEntry)}
                        <p className={`type-ui ${surveyMutedTextClassName}`}>
                          {index + 1}/{questions.length}
                        </p>
                        <h2 className="type-h4 text-kc-black dark:text-kc-gray-light">
                          {getLocalizedPulseQuestionText(
                            questionEntry?.question,
                            i18n.language
                          )}
                        </h2>
                        {questionEntry?.question?.type === 'multiselect' &&
                        questionEntry?.question?.maxOptions ? (
                          <p className={`type-body ${surveyMutedTextClassName}`}>
                            {t('pulse.survey.max.options', {
                              max: questionEntry.question.maxOptions,
                            })}
                          </p>
                        ) : null}
                      </div>
                      {renderQuestionInput(questionEntry, index)}
                    </div>
                  ))}
                </div>
              ) : isReviewStep ? (
                <div className="space-y-4">
                  <div className="rounded-kcInner border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-1 text-left">
                        <h3 className="type-h4 text-kc-black dark:text-kc-gray-light">
                          {t('pulse.survey.reviewTitle')}
                        </h3>
                        <p className={`type-body ${surveyMutedTextClassName}`}>
                          {t('pulse.survey.reviewDescription')}
                        </p>
                      </div>
                      <KincodeButton
                        className="w-full shrink-0 sm:w-auto"
                        colorHex={colorHex}
                        data-testid="survey-pulse-survey-review-submit"
                        disabled={isLoading}
                        theme={buttonTheme}
                        onClick={handleSubmitSurvey}
                      >
                        {t('submit')}
                      </KincodeButton>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {questions.map((questionEntry, index) => {
                      const questionKey = getQuestionKey(questionEntry, index);
                      const answerLabel = formatSurveyAnswerDisplayLabel(
                        questionEntry,
                        answersByQuestionId[questionKey],
                        {
                          language: i18n.language,
                          rnaLabel: getSurveyRnaLabel(
                            questionEntry?.question,
                            t('pulse.survey.rna')
                          ),
                        }
                      );

                      return (
                        <div
                          key={`${questionKey}-summary`}
                          className="rounded-kcInner border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900/60"
                        >
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            {index + 1}/{questions.length}
                          </div>
                          <div className="type-h5 mt-1 text-kc-black dark:text-kc-gray-light">
                            {getLocalizedPulseQuestionText(
                              questionEntry?.question,
                              i18n.language
                            )}
                          </div>
                          <div className={`type-body mt-1 ${surveyMutedTextClassName}`}>
                            {answerLabel}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div
                  key={currentQuestionKey ?? currentStep}
                  className="animate-survey-question-in space-y-5"
                >
                  <div className="space-y-2">
                    {renderQuestionTaxonomyPills(currentQuestionEntry)}
                    <h2 className="type-h4 text-left text-kc-black dark:text-kc-gray-light">
                      {getLocalizedPulseQuestionText(
                        currentQuestionEntry?.question,
                        i18n.language
                      )}
                    </h2>
                  </div>
                  {currentQuestionEntry?.question?.type === 'multiselect' &&
                  currentQuestionEntry?.question?.maxOptions ? (
                    <p className={`type-body text-left ${surveyMutedTextClassName}`}>
                      {t('pulse.survey.max.options', {
                        max: currentQuestionEntry.question.maxOptions,
                      })}
                    </p>
                  ) : null}
                  {renderQuestionInput(currentQuestionEntry, currentStep)}
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-col gap-4 border-t border-border-default px-5 py-4 dark:border-border-default-dark sm:flex-row sm:items-center sm:justify-between lg:px-6">
              <div
                className={`type-body flex items-center gap-2 text-left ${surveyMutedTextClassName}`}
              >
                <UnifiedIcon className="h-4 w-4 shrink-0" name="FloppyDisk" />
                <span>{t('pulse.survey.savedOnDevice')}</span>
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {isListView ? (
                  <KincodeButton
                    className="w-full sm:w-auto"
                    colorHex={colorHex}
                    disabled={isLoading || !canSubmitSurvey}
                    theme={buttonTheme}
                    onClick={handleSubmitSurvey}
                  >
                    {t('submit')}
                  </KincodeButton>
                ) : (
                  <>
                    {currentStep > 0 ? (
                      <KincodeButton
                        className="w-full sm:w-auto"
                        colorHex={colorHex}
                        theme={buttonTheme}
                        variant="tertiary"
                        onClick={handlePrevious}
                      >
                        <UnifiedIcon className="h-4 w-4" name="ArrowLeft" library="phosphor" />
                        {t('back')}
                      </KincodeButton>
                    ) : null}

                    {isReviewStep ? (
                      <KincodeButton
                        className="w-full sm:w-auto"
                        colorHex={colorHex}
                        disabled={isLoading}
                        theme={buttonTheme}
                        onClick={handleSubmitSurvey}
                      >
                        {t('submit')}
                      </KincodeButton>
                    ) : !shouldAutoAdvanceSurveyStepper(currentQuestionEntry) ||
                      currentQuestionAnswered ? (
                      <KincodeButton
                        className="w-full sm:w-auto"
                        colorHex={colorHex}
                        disabled={!currentQuestionAnswered || isLoading}
                        theme={buttonTheme}
                        onClick={handleNext}
                      >
                        {currentStep === questions.length - 1
                          ? t('pulse.survey.review')
                          : t('next')}
                        <UnifiedIcon className="h-4 w-4" name="ArrowRight" library="phosphor" />
                      </KincodeButton>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
      <div className={surveyLegalFooterClassName}>
        <Footer color="bg-transparent" />
      </div>
    </div>
  );
}

export default PulseSurvey;
